import { Color, Spacing } from '@persona-id/design-tokens';
import {
  CursorIcon,
  DevicesIcon,
  HandIcon,
  RotateCcwIcon,
  RotateCwIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '@persona-id/design-tokens/icons';
import { ComponentProps, ReactNode } from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';

import ZoomInput from 'dashboard/components/GraphToolbar/ZoomInput';
import ScreenDimensionsInput from 'dashboard/components-lib/EditorToolbar/ScreenDimensionsInput';
import ToolbarControlButton from 'dashboard/components-lib/ToolbarControlButton';
import Popover from 'design-system/components/Popover';
import { Paragraph } from 'design-system/components/Text';

const EditorToolbarContainer = styled.div<{ additionalCss?: FlattenSimpleInterpolation }>`
  background-color: ${({ theme }) => theme.color.background.primary};
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  border: 1px solid ${Color.CoreSlate100};

  ${({ additionalCss }) => additionalCss}
`;

const EditorLeftNavContainer = styled.div`
  display: flex;
  align-items: center;
  border-right: 1px solid ${Color.CoreSlate100};
  padding: 0px ${Spacing[2]};
  gap: ${Spacing[1]};
`;

const ToolbarList = styled('div')`
  display: flex;
  gap: ${Spacing[1]};
  flex-direction: row;
  list-style-type: none;
  padding-left: ${Spacing[2]};
  padding-right: ${Spacing[2]};
  border-width: 0;
`;

const EditorRightNavContainer = styled.div<{ showBorder: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0px ${Spacing[2]};
  ${({ showBorder }) =>
    showBorder &&
    css`
      border-left: 1px solid ${Color.CoreSlate100};
    `}
  gap: ${Spacing[1]};
`;

const FloatingRightContainer = styled.div`
  margin-left: auto;
`;

const ToolInputContainer = styled('div')`
  padding: ${Spacing[2]};
  margin: 0;

  & > div {
    height: 32px;
    padding: 3px ${Spacing[2]} ${Spacing[2]} ${Spacing[1]};
  }

  & > div,
  input {
    font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  }
`;

export enum ToolbarControlItem {
  Pan = 'pan',
  Select = 'select',
  Undo = 'undo',
  Redo = 'redo',
  ZoomIn = 'zoomIn',
  ZoomOut = 'zoomOut',
}

const ToolbarItemUI: Record<ToolbarControlItem, { icon: ReactNode; tooltipText: string }> = {
  [ToolbarControlItem.Select]: { icon: <CursorIcon />, tooltipText: 'Select mode' },
  [ToolbarControlItem.Pan]: {
    icon: <HandIcon />,
    tooltipText: 'Pan mode',
  },
  [ToolbarControlItem.Undo]: { icon: <RotateCcwIcon />, tooltipText: 'Undo' },
  [ToolbarControlItem.Redo]: { icon: <RotateCwIcon />, tooltipText: 'Redo' },
  [ToolbarControlItem.ZoomOut]: { icon: <ZoomOutIcon />, tooltipText: 'Zoom out' },
  [ToolbarControlItem.ZoomIn]: { icon: <ZoomInIcon />, tooltipText: 'Zoom in' },
};

type ControlOptions = {
  active?: boolean;
  onClick?: () => void;
  disabled?: boolean;
};

type EditToolbarControlItems = {
  [key in ToolbarControlItem]?: ControlOptions;
};

export interface EditorToolbarProps {
  id: string;
  zoomInput?: {
    percentage: number;
    onChange: (num: number) => void;
  };
  leftNavItems?: ReactNode;
  rightNavItems?: ReactNode;
  showRightNavContainerBorder?: boolean;
  controls?: EditToolbarControlItems;
  extraControlsLeft?: ReactNode;
  extraControlsRight?: ReactNode;
  showScreenDimensions?: boolean;
  screenDimensionsInput?:
    | (ComponentProps<typeof ScreenDimensionsInput> & { disabled?: boolean })
    | null;
  additionalCss?: FlattenSimpleInterpolation;
}

const EditorToolbar = ({
  id,
  leftNavItems,
  rightNavItems,
  showRightNavContainerBorder = false,
  controls,
  zoomInput,
  extraControlsLeft,
  extraControlsRight,
  screenDimensionsInput,
  additionalCss,
}: EditorToolbarProps) => {
  return (
    <EditorToolbarContainer additionalCss={additionalCss}>
      {leftNavItems && (
        <EditorLeftNavContainer data-test='editor-toolbar-left-nav'>
          {leftNavItems}
        </EditorLeftNavContainer>
      )}
      <ToolbarList>
        {controls &&
          Object.entries(controls).map(([controlItem, controlOptions]) => {
            const controlUI = ToolbarItemUI[controlItem as ToolbarControlItem];
            const { disabled, active, onClick } = controlOptions;
            if (!onClick) {
              return null;
            }
            return (
              <ToolbarControlButton
                tooltipText={controlUI.tooltipText}
                key={`${controlItem}-control`}
                id={`${id}__button-${controlItem}`}
                active={active}
                disabled={disabled}
                onClick={onClick}
                icon={controlUI.icon}
              />
            );
          })}
        {zoomInput && (
          <ToolInputContainer id={`${id}__input-zoom`} data-test={`${id}__input-zoom`}>
            <ZoomInput
              zoomPercentage={zoomInput.percentage}
              handleZoomPercentageChange={zoomInput.onChange}
            />
          </ToolInputContainer>
        )}
        {screenDimensionsInput && (
          <Popover
            contentProps={{
              align: 'start',
              side: 'bottom',
              // prevents screen editor hover events from being triggered when hovering over popover
              style: { zIndex: 1 },
            }}
          >
            <Popover.Trigger asChild>
              <ToolbarControlButton
                tooltipText={`Adjust Preview Dimensions ${
                  screenDimensionsInput.disabled ? '(Inline flow only)' : ''
                }`}
                key='screen-dimensions-control'
                id={`${id}__button-screen-dimensions`}
                icon={<DevicesIcon />}
                disabled={screenDimensionsInput.disabled}
              />
            </Popover.Trigger>
            <Paragraph color='primary' weight='bold' data-test={`${id}__label-screen-dimensions`}>
              Preview Dimensions
            </Paragraph>
            <ScreenDimensionsInput {...screenDimensionsInput} />
          </Popover>
        )}
        {extraControlsLeft}
        <FloatingRightContainer>{extraControlsRight}</FloatingRightContainer>
      </ToolbarList>
      {rightNavItems && (
        <EditorRightNavContainer
          data-test='editor-toolbar-right-nav'
          showBorder={showRightNavContainerBorder}
        >
          {rightNavItems}
        </EditorRightNavContainer>
      )}
    </EditorToolbarContainer>
  );
};

export default EditorToolbar;
